import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SharedPageLayout } from './SharedPageLayout';
import './Home.css';
import './CompleteArticleList.css';

// Resolves the base public URL for static asset requests.
const getBaseUrl = () => process.env.PUBLIC_URL || '';

// Builds the article detail route path for a given index entry.
const buildArticleLink = (indexItem) => {
    const slug = indexItem?.slug || indexItem?.id;
    return `/blog/${slug}`;
};

// Builds a human-readable label for an article list item.
const buildArticleLabel = (indexItem) => {
    return indexItem?.title || indexItem?.videogame_name || indexItem?.id || 'Untitled article';
};

// Checks if the current index entry matches the provided text filter query across all fields.
const matchesSearchQuery = (indexItem, normalizedSearch) => {
    if (!normalizedSearch) {
        return true;
    }

    const indexValues = Object.values(indexItem || {});
    let indexMatch = false;

    for (const value of indexValues) {
        if (value === null || value === undefined) {
            continue;
        }

        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            if (String(value).toLowerCase().includes(normalizedSearch)) {
                indexMatch = true;
                break;
            }
            continue;
        }

        try {
            if (JSON.stringify(value).toLowerCase().includes(normalizedSearch)) {
                indexMatch = true;
                break;
            }
        } catch (error) {
            indexMatch = indexMatch || false;
        }
    }

    return indexMatch;
};

// Renders the complete article list page with search filtering.
export function CompleteArticleList() {
    const [articles, setArticles] = useState([]);
    const [status, setStatus] = useState('loading');
    const [errorMessage, setErrorMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    // Loads the article index metadata for the complete list.
    useEffect(() => {
        let isActive = true;

        // Fetches the index list from the public folder.
        const loadArticles = async () => {
            try {
                const indexResponse = await fetch(`${getBaseUrl()}/articles/index.json`);
                if (!indexResponse.ok) {
                    throw new Error('Unable to load article index.');
                }
                const indexItems = await indexResponse.json();

                if (isActive) {
                    setArticles(Array.isArray(indexItems) ? indexItems : []);
                    setStatus('ready');
                }
            } catch (error) {
                if (isActive) {
                    setErrorMessage(error.message || 'Unable to load articles.');
                    setStatus('error');
                }
            }
        };

        loadArticles();

        return () => {
            isActive = false;
        };
    }, []);

    const navLinks = [
        { href: '/', label: 'Home' },
        { href: '/play-games-now', label: 'Play games now', accent: true }
    ];

    const normalizedQuery = searchQuery.trim().toLowerCase();

    // Filters the loaded index entries by the search query across all fields.
    const filteredArticles = useMemo(() => {
        return articles.filter((indexItem) => matchesSearchQuery(indexItem, normalizedQuery));
    }, [articles, normalizedQuery]);

    // Updates the search query state whenever the input value changes.
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    return (
        <SharedPageLayout
            brand="GIUGGIOLI.NET"
            links={navLinks}
            footerText="Arcade Vault • Powered by Giuggioli.Net"
        >
            <header className="retro-hero">
                <div className="retro-hero__content">
                    <p className="retro-kicker">archive index</p>
                    <h1 className="retro-title">Complete Article List</h1>
                    <p className="retro-subtitle">
                        Browse every article in the arcade archive, or filter the list using a keyword search across titles, tags,
                        games, and metadata.
                    </p>
                </div>
                <div className="retro-hero__panel">
                    <div className="retro-panel__label">How to use</div>
                    <p className="retro-panel__text">
                        Start typing to filter across the full index. Click any title to jump straight into the article detail.
                    </p>
                </div>
            </header>

            <section className="retro-year-filter">
                <div className="retro-year-filter__label">Search the archive</div>
                <div className="retro-year-filter__text">
                    <input
                        className="retro-year-filter__input"
                        type="search"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        placeholder="Search by title, game, tag"
                        aria-label="Search all articles"
                    />
                </div>
                <div className="retro-year-filter__hint">
                    {status === 'ready' ? `Showing ${filteredArticles.length} of ${articles.length} articles` : ''}
                </div>
            </section>

            <section className="retro-feed">
                <div className="retro-feed__header">
                    <h2>Full Archive</h2>
                    <p>Every retro story, collected in one place.</p>
                </div>

                {status === 'loading' && (
                    <div className="retro-state">Loading transmissions...</div>
                )}

                {status === 'error' && (
                    <div className="retro-state retro-state--error">{errorMessage}</div>
                )}

                {status === 'ready' && (
                    <div className="retro-article-list">
                        {filteredArticles.length === 0 ? (
                            <div className="retro-state">No articles match that search.</div>
                        ) : (
                            <ul className="retro-article-list__items">
                                {filteredArticles.map((article) => (
                                    <li className="retro-article-list__item" key={article.id}>
                                        <Link className="retro-article-list__link" to={buildArticleLink(article)}>
                                            {buildArticleLabel(article)}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </section>
        </SharedPageLayout>
    );
}
